import { forwardRef, Fragment, useId, useState } from "react";
import styled from "styled-components";
import {
  Button,
  DateInput,
  Dropdown,
  DropdownItem,
  SingleSelect,
  TextInput,
  VisuallyHidden,
} from "suomifi-ui-components";
import { IconChevronDown, IconChevronUp } from "suomifi-icons";
import { useTranslation } from "react-i18next";
import { getKansalaisuusOptions } from "../utils/kansalaisuudet";

/**
 * Tarkennetun haun lisäkentät. Toggle-painike paljastaa kentät perushakukentän
 * alle SAMAAN hakulomakkeeseen — ei omaa Hae-painiketta, vaan lomakkeen yksi
 * yhteinen Hae ajaa perushaun ja lisäkentät yhdessä (JA-haku).
 *
 * Ohjattu komponentti: kenttien arvot ja onChange tulevat vanhemmalta
 * (KayttajaHaku), joka omistaa hakutilan ja suorittaa haun.
 *
 * Kaksi palstaa: henkilötiedot vasemmalla, asiakirjan tiedot oikealla; kapealla
 * näytöllä allekkain. Sama järjestys kuin käyttäjän Asiakirjat-kortilla.
 */

type Kentta = { key: string; labelKey: string };

const HENKILO_KENTAT: Kentta[] = [
  { key: "sukunimi", labelKey: "common:sukunimi" },
  { key: "etunimet", labelKey: "common:etunimet" },
  { key: "syntymaaika", labelKey: "common:syntymaaika" },
];

const ASIAKIRJA_KENTAT: Kentta[] = [
  { key: "asiakirjanNumero", labelKey: "common:asiakirjanNumero" },
  { key: "myontajamaa", labelKey: "common:myontajamaa" },
  { key: "voimassaolopaiva", labelKey: "common:voimassaolopaiva" },
];

const Wrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing.s};
`;

const Panel = styled.div`
  margin-top: ${({ theme }) => theme.spacing.s};
`;

/* Sama tyyli kuin Suomi.fi:n HintText (bodyTextSmall + blackBase), jotta
   ohjetekstit ovat yhdenmukaiset kentän hintTextin kanssa. */
const Ohje = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.blackBase};
  margin: 0 0 ${({ theme }) => theme.spacing.m};
`;

/* Kaksi palstaa; kapealla näytöllä (~75rem) allekkain. Iso palstaväli (xxl),
   jotta oikean palstan label ei ryhmity vasemman palstan kentän kanssa. */
const Palstat = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xxl};
  align-items: start;

  @media (max-width: 75rem) {
    grid-template-columns: 1fr;
  }
`;

/* Kiinteä label-sarake (pisimmän labelin mukaan) → kaikki labelit
   samanlevyisiä ja tekstikentät alkavat samasta kohdasta, molemmissa
   palstoissa ja myös allekkain pinottuna. Tiivis label–kenttä-väli. */
const Palsta = styled.div`
  display: grid;
  grid-template-columns: 8rem 1fr;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: start;
  row-gap: ${({ theme }) => theme.spacing.m};
`;

/* Näkyvä label vasemmalla — aria-hidden, koska varsinainen saavutettava label
   tulee TextInputin labelText-propista (labelMode="hidden"). */
const FieldLabel = styled.span`
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const SingleSelectStyled = styled(SingleSelect)`
  margin-top: -28px; /* kompensoi labelin ja kentän välin, koska label on erillinen VisuallyHidden FieldLabel */
`;

const TarkennettuHaku = forwardRef<
  HTMLDivElement,
  {
    kentat: Record<string, string>;
    onChange: (key: string, arvo: string) => void;
  }
>(({ kentat, onChange }, ref) => {
  const { t, i18n } = useTranslation(["kayttajaLista", "common"]);
  const [auki, setAuki] = useState(false);
  const panelId = useId();

  const sukupuoliOptions = [
    { label: t("common:sukupuolet.nainen"), value: "FEMALE" },
    { label: t("common:sukupuolet.mies"), value: "MALE" },
    { label: t("common:sukupuolet.muu"), value: "OTHER" },
    { label: t("common:sukupuolet.eiIlmoitettu"), value: "UNDISCLOSED" },
  ];

  const kansalaisuusOptions = getKansalaisuusOptions(i18n.language).map(
    (option) => ({
      labelText: option.label,
      uniqueItemId: option.value,
    }),
  );

  function renderKentta(kentta: Kentta) {
    const label = t(kentta.labelKey);
    if (kentta.key === "myontajamaa") {
      return (
        <Fragment key={kentta.key}>
          <FieldLabel>{t("common:myontajamaa")}</FieldLabel>
          <SingleSelectStyled
            labelText={
              <VisuallyHidden>{t("common:myontajamaa")}</VisuallyHidden>
            }
            items={kansalaisuusOptions}
            selectedItem={
              kansalaisuusOptions.find(
                (o) => o.uniqueItemId === kentat["myontajamaa"],
              ) ?? undefined
            }
            onItemSelect={(item) => onChange("myontajamaa", item ?? "")}
            clearButtonLabel={t("common:tyhjenna")}
            allowItemAddition={false}
            ariaOptionsAvailableText={t("common:valitseVaihtoehto")}
            noItemsText={t("common:eiTuloksia")}
            fullWidth
          />
        </Fragment>
      );
    }
    if (kentta.key === "syntymaaika" || kentta.key === "voimassaolopaiva") {
      const parseDate = (input: string): string => {
        let formattedDate = "";
        const trimmedInput = input.trim();

        if (!trimmedInput) return "";

        let date: Date | null = null;

        // Try DD.MM.YYYY or DD.M.YYYY format (Finnish - calendar or manual)
        if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(trimmedInput)) {
          const [day, month, year] = trimmedInput.split(".");
          date = new Date(
            `${year}-${month.padStart(2, "0")}-${day.padStart(
              2,
              "0",
            )}T00:00:00Z`,
          );
        }
        // Try YYYY-MM-DD format
        else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(trimmedInput)) {
          const [year, month, day] = trimmedInput.split("-");
          date = new Date(
            `${year}-${month.padStart(2, "0")}-${day.padStart(
              2,
              "0",
            )}T00:00:00Z`,
          );
        }
        // Try calendar picker format (already YYYY-MM-DD)
        else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedInput)) {
          date = new Date(trimmedInput + "T00:00:00Z");
        }

        // Format as DD.MM.YYYY if valid date
        if (date && !isNaN(date.getTime())) {
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, "0");
          const day = String(date.getUTCDate()).padStart(2, "0");
          formattedDate = `${day}.${month}.${year}`;
        }

        return formattedDate;
      };

      return (
        <Fragment key={kentta.key}>
          <FieldLabel aria-hidden="true">{label}</FieldLabel>
          <div
            onBlur={(event) => {
              // Only process if focus moved completely outside the date input wrapper
              const relatedTarget = event.relatedTarget as Node;

              if (!event.currentTarget.contains(relatedTarget)) {
                const input = event.currentTarget.querySelector(
                  "input",
                ) as HTMLInputElement;
                if (input) {
                  const formatted = parseDate(input.value);
                  if (formatted) {
                    onChange(kentta.key, formatted);
                  }
                }
              }
            }}
          >
            <DateInput
              labelText={label}
              labelMode="hidden"
              datePickerEnabled
              value={kentat[kentta.key] ?? ""}
              onChange={({ value }: { value: string }) => {
                onChange(kentta.key, value);
              }}
              minDate={new Date(1900, 0, 1)}
              maxDate={new Date()}
              fullWidth
            />
          </div>
        </Fragment>
      );
    }
    // Default case for other fields (TextInput)
    return (
      <Fragment key={kentta.key}>
        <FieldLabel aria-hidden="true">{label}</FieldLabel>
        <TextInput
          labelText={label}
          labelMode="hidden"
          value={kentat[kentta.key] ?? ""}
          onChange={(v) => onChange(kentta.key, (v ?? "").toString())}
          fullWidth
        />
      </Fragment>
    );
  }

  return (
    <Wrap>
      <Button
        variant="secondaryNoBorder"
        iconRight={auki ? <IconChevronUp /> : <IconChevronDown />}
        aria-expanded={auki}
        aria-controls={panelId}
        onClick={() => setAuki((v) => !v)}
      >
        {t("tarkennettu.avaa")}
      </Button>

      {auki && (
        <Panel id={panelId}>
          <Ohje>{t("tarkennettu.ohje")}</Ohje>
          <Palstat>
            <Palsta>
              {HENKILO_KENTAT.map(renderKentta)}
              {/* Sukupuoli dropdown */}
              <FieldLabel aria-hidden="true">
                {t("common:sukupuoli")}
              </FieldLabel>
              <Dropdown
                labelText={t("common:sukupuoli")}
                labelMode="hidden"
                value={kentat["sukupuoli"] ?? ""}
                onChange={(value) => onChange("sukupuoli", value)}
                fullWidth
              >
                {sukupuoliOptions.map((o) => (
                  <DropdownItem key={o.value} value={o.value}>
                    {o.label}
                  </DropdownItem>
                ))}
              </Dropdown>

              {/* Kansalaisuus single select */}
              <FieldLabel>{t("common:kansalaisuus")}</FieldLabel>

              <SingleSelectStyled
                labelText={
                  <VisuallyHidden>{t("common:kansalaisuus")}</VisuallyHidden>
                }
                items={kansalaisuusOptions}
                selectedItem={
                  kansalaisuusOptions.find(
                    (o) => o.uniqueItemId === kentat["kansalaisuus"],
                  ) ?? undefined
                }
                onItemSelect={(item) => onChange("kansalaisuus", item ?? "")}
                clearButtonLabel={t("common:tyhjenna")}
                allowItemAddition={false}
                ariaOptionsAvailableText={t("common:valitseVaihtoehto")}
                noItemsText={t("common:eiTuloksia")}
                fullWidth
              />
            </Palsta>
            <Palsta>{ASIAKIRJA_KENTAT.map(renderKentta)}</Palsta>
          </Palstat>
        </Panel>
      )}
    </Wrap>
  );
});
TarkennettuHaku.displayName = "TarkennettuHaku";

export default TarkennettuHaku;
